import { useDebounced } from '@platewell/query';
import { ADMIN_PAGE_SIZE, type AdminListResponse } from '@platewell/schema';
import { useEffect, useRef, useState } from 'react';
import { formatRange } from '../components/data-table.js';
import type { AdminListParams } from './api.js';
import type { AdminListSearch } from '../router.js';

/**
 * The plumbing both admin lists share: URL-as-state, a debounced search box, one in-flight
 * fetch, and the two page-reset rules.
 *
 * The URL owns page, sort, direction and query, so an agent can paste a link at a colleague
 * and the back button walks their steps. `navigate` is passed in rather than derived here
 * because TanStack types it per route.
 */
export interface UseAdminListOptions<T> {
  search: AdminListSearch;
  defaultSort: string;
  fetchPage: (params: AdminListParams) => Promise<AdminListResponse<T>>;
  navigate: (update: (previous: AdminListSearch) => AdminListSearch, replace: boolean) => void;
  errorLabel: string;
}

export interface AdminListState<T> {
  rows: T[];
  total: number;
  page: number;
  pageSize: number;
  sort: string;
  dir: 'asc' | 'desc';
  q: string;
  loading: boolean;
  error: string | null;
  /**
   * "1–30 of 214", or "No results" — but empty until the first response lands, so the
   * heading never claims there is nothing while the skeleton rows are still loading.
   */
  rangeLabel: string;
  /** The live value of the search box, which the URL only overrides from outside. */
  term: string;
  setTerm: (value: string) => void;
  onSortChange: (key: string) => void;
  onPageChange: (page: number) => void;
}

export function useAdminList<T>({
  search,
  defaultSort,
  fetchPage,
  navigate,
  errorLabel,
}: UseAdminListOptions<T>): AdminListState<T> {
  const page = search.page ?? 1;
  const sort = search.sort ?? defaultSort;
  const dir = search.dir ?? 'asc';
  const q = search.q ?? '';

  // Typing owns the input so it never lags the keyboard or fights the debounce; the URL
  // takes over only when it changed from somewhere else — back button, or a cold deep link.
  const [term, setTerm] = useState(q);
  const pushedTerm = useRef(q);
  useEffect(() => {
    if (q !== pushedTerm.current) {
      pushedTerm.current = q;
      setTerm(q);
    }
  }, [q]);

  const debounced = useDebounced(term, 250);
  useEffect(() => {
    if (debounced === q) return;
    pushedTerm.current = debounced;
    // Replaced rather than pushed: every keystroke should not become a history entry.
    navigate(
      (previous) => ({ ...previous, q: debounced === '' ? undefined : debounced, page: undefined }),
      true,
    );
  }, [debounced, q, navigate]);

  const [data, setData] = useState<AdminListResponse<T> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void fetchPage({ page, pageSize: ADMIN_PAGE_SIZE, sort, dir, q })
      .then((body) => {
        if (cancelled) return;
        setData(body);
        setError(null);
        // The server clamps the requested page against the real total, so a search that
        // shrinks the result set cannot strand the caller past the end. This has to compare
        // against the page *this* request asked for: comparing against whatever response is
        // currently in state would see the previous page's number the instant the pager is
        // clicked, and drag the URL straight back.
        if (body.page !== page) {
          navigate(
            (previous) => ({ ...previous, page: body.page === 1 ? undefined : body.page }),
            true,
          );
        }
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setError(cause instanceof Error ? cause.message : errorLabel);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchPage, page, sort, dir, q, errorLabel, navigate]);

  const total = data?.total ?? 0;

  return {
    rows: data?.rows ?? [],
    total,
    page,
    pageSize: ADMIN_PAGE_SIZE,
    sort,
    dir,
    q,
    loading,
    error,
    rangeLabel: data == null ? '' : formatRange(page, ADMIN_PAGE_SIZE, total),
    term,
    setTerm,
    // A new column starts ascending; the active column toggles. Either way, back to page 1.
    onSortChange: (key: string) =>
      navigate(
        (previous) => ({
          ...previous,
          sort: key,
          dir: key === sort && dir === 'asc' ? 'desc' : 'asc',
          page: undefined,
        }),
        false,
      ),
    onPageChange: (next: number) =>
      navigate((previous) => ({ ...previous, page: next === 1 ? undefined : next }), false),
  };
}
